import React, { memo, useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  Label,
  ResponsiveContainer,
} from 'recharts';
import { useHistory, useLocation } from 'react-router-dom';
import { MedisanteTargetSystem } from '../../TargetSystems/types';
import { groupByValue } from '../../../utils/helpers';
import { MedisanteOrgReport } from '../types';
import Visible from '../../../components/Visible/Visible';

const useStyles = makeStyles((theme: Theme) => ({
  pullRight: {
    textAlign: 'right',
  },
  wrapper: {
    display: 'block',
    padding: theme.spacing(2),
  },

  secondRowWidgets: {
    height: '75px',
    minHeight: '75px',
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

const statusLabels = {
  '1XX': 'Info',
  '2XX': 'Success',
  '3XX': 'Redirection',
  '4XX': 'Client Error',
  '5XX': 'Server Error',
  '9XX': 'Custom',
};

interface Props {
  targetSystems: MedisanteTargetSystem[];
  type: 'device' | 'target-system';
  reports?: MedisanteOrgReport[];
  disabledClick?: boolean;
}

const statusCodeColors = (statusCode: string) => {
  if (statusCode === statusLabels['2XX']) return '#32D17E';
  if (statusCode === statusLabels['1XX']) return '#32bed1';
  else {
    return 'rgb(250, 84, 54)';
  }
};

const calculateTsDevicesInfo = (
  ts: MedisanteTargetSystem[],
  reportsByOwner: Map<string, MedisanteOrgReport>
) => {
  let count = 0;
  let ids: string[] = [];
  ts.forEach((t) => {
    if (
      t.owner &&
      reportsByOwner.get(t.owner) &&
      reportsByOwner.get(t.owner)?.targetSystemDevices &&
      reportsByOwner.get(t.owner)?.targetSystemDevices[t.id]
    ) {
      const status = t.lastStatusCode ? t.lastStatusCode : 0;
      count = reportsByOwner.get(t.owner)?.statusDevicesCount[status] || 0;
      ids.push(t.id);
    }
  });
  return { count, ids };
};

function StatusCodePieChart({
  targetSystems,
  type = 'target-system',
  reports,
  disabledClick = false,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [reportsByOwner, setReportsByOwner] = useState<
    Map<string, MedisanteOrgReport>
  >(new Map());
  useEffect(() => {
    let outputMap: Map<string, MedisanteOrgReport> = new Map();
    reports?.forEach((r) => {
      outputMap.set(r.owner, r);
    });
    setReportsByOwner(outputMap);
  }, [reports]);

  const chartData = Object.entries(
    groupByValue(
      targetSystems.filter((ts) => !!ts.lastStatusCode),
      (ts) => `${ts.lastStatusCode!.toString().charAt(0)}XX`
    )
  )
    .map(([key, ts]) => {
      const info = calculateTsDevicesInfo(ts, reportsByOwner);
      return {
        //@ts-ignore
        name: statusLabels[key] ? statusLabels[key] : key,
        statusShort: key,
        targetSystems: info.ids,
        val: type === 'device' ? info.count : ts.length,
      };
    })
    .filter((data) => data.val !== 0);

  const renderLabel = () => {
    const totalSum = chartData.reduce((curr, item) => curr + item.val, 0);
    return (
      <g>
        <foreignObject width="130" height="80" x={65} y={100}>
          <Typography variant="body2" color="textSecondary" align="center">
            {`Available for`}
            <br />
            {`${totalSum} ${type === 'device' ? 'device' : 'webhook'}${
              totalSum === 1 ? '' : 's'
            }`}
          </Typography>
        </foreignObject>
      </g>
    );
  };

  return (
    <Visible when={chartData.reduce((curr, item) => curr + item.val, 0) !== 0}>
      <Paper className={classes.wrapper}>
        <Typography variant="h6" align="left">
          Status Codes
        </Typography>
        <ResponsiveContainer
          height={320}
          width={260}
          className={classes.container}
        >
          <PieChart>
            <Pie
              data={chartData}
              dataKey="val"
              isAnimationActive={true}
              innerRadius={90}
              outerRadius={110}
              paddingAngle={1.5}
            >
              {chartData.map((cat) => (
                // @ts-ignore
                <Cell
                  stroke="transparent"
                  key={cat.name}
                  fill={statusCodeColors(cat.name)}
                  cursor="pointer"
                  onClick={() => {
                    if (disabledClick) return;
                    const statusCodes = Object.entries(
                      groupByValue(
                        targetSystems.filter((ts) => !!ts.lastStatusCode),
                        (ts) => ts.lastStatusCode!.toString()
                      )
                    )
                      .filter(
                        ([key]) => key.charAt(0) === cat.statusShort.charAt(0)
                      )
                      .map(([key]) => `last_status_code=${key}`)
                      .join('&');

                    switch (type) {
                      case 'device':
                        history.push(
                          `/devices${
                            location.search ? location.search + '&' : '?'
                          }${statusCodes}`
                        );
                        break;
                      case 'target-system':
                        history.push(`/webhooks?${statusCodes}`);
                        break;
                    }
                  }}
                />
              ))}
              <Label content={renderLabel} />
            </Pie>
            <Tooltip />
            <Legend
              key="label"
              verticalAlign="bottom"
              width={260}
              height={55}
            />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    </Visible>
  );
}

export default memo(StatusCodePieChart);
